import React from 'react';
import {observer} from "mobx-react";
import {GamblingSessionLimitsBox} from "./boxes/gambling-session-limits.box";
import {IGamblingSessionLimitsSetup} from '../../../services/gambling-session/gambling-session-limits-setup.interface';
import {IModalDialogHandler} from '../../../services/dialog/dialog.service.interface';
import {DropDownsGroupBox} from "./boxes/drop-downs-group.box";
import {NullableNumber, NullableString} from '../../../../common/types/nullable-types';
import {OrbitComponentWithState} from '../../base-components/orbit-component-with-state/orbit-component-with-state';
import {Check, TimeSpan} from "@sparkware/app-utils";
import {LossLimitDropDownComponent} from "./parts/loss-limit-drop-down.component";
import {TimeLimitDropDownComponent} from "./parts/time-limit-drop-down.component";
import {IntervalReminderDropDownComponent} from "./parts/interval-reminder-drop-down.component";
import {RestrictionPeriodsDropDownComponent} from "./parts/restriction-periods-drop-down.component";
import {FooterBox} from "./boxes/footer.box";
import {Button} from '../../../basic-ui-components/button/base/button.component';
import {parseCommaSeparatedNumbers} from '../../../../utils/string-helpers';
import {DropDownsSeparatorBox} from "./boxes/drop-downs-separator.box";
import {RegulatoryTextBox} from "./boxes/regulatory-text.box";
import {GAMBLING_SESSION_LIMITS_RESPONSIBLE_GAMING_LINK} from './css-constants';
import {GamblingSessionMessageBox} from './boxes/gambling-session-message.box';
import {GamblingSessionWarnMessageBox} from './boxes/gambling-session-warn-message.box';
import {DividerBox} from '../../../basic-ui-components/layout/divider/divider.box';
import {CyClass} from "../../../../common/cy-classes";

interface IGamblingSessionSetupProps {
    onSetLimits: (gameLimits: IGamblingSessionLimitsSetup) => void;
    dialogHandler: IModalDialogHandler;
    lastBetActivity?: NullableString;
}

interface IGamblingSessionLimitsInternalData {
    lossLimitAmount: number;
    sessionDurationLimit: number;
    reminderInterval: number;
    restrictionPeriod?: number;
}

interface IGamblingSessionSetupState {
    selectedLimits: Partial<IGamblingSessionLimitsInternalData>;
    isValidationEnabled: boolean;
    lossLimitsErrorMessage: NullableString;
    timeLimitsErrorMessage: NullableString;
}



@observer
export class GamblingSessionSetupComponent extends OrbitComponentWithState<IGamblingSessionSetupProps, IGamblingSessionSetupState> {

    constructor(props: IGamblingSessionSetupProps) {
        super(props);
        this.state = {
            selectedLimits: {},
            isValidationEnabled: false,
            lossLimitsErrorMessage: null,
            timeLimitsErrorMessage: null
        }
    }

    private _onAfterValidationEnabled = () => {

        if(!this._performValidation()) {
            return;
        }

        this.props.dialogHandler.close();

        let intervalReminder = this.state.selectedLimits.reminderInterval;
        if(Check.isNullOrUndefined(intervalReminder)) {
            intervalReminder = TimeSpan.fromMinutes(parseCommaSeparatedNumbers(this.services.configuration.servicesConfigs.Regulation.Spain.IntervalReminderInMinutes)[0]).totalSeconds;
        }

        this.props.onSetLimits({
            lossLimitAmount: this.state.selectedLimits.lossLimitAmount,
            sessionDurationLimit: TimeSpan.fromSeconds(this.state.selectedLimits.sessionDurationLimit),
            reminderInterval: TimeSpan.fromSeconds(intervalReminder),
            restrictionPeriod: TimeSpan.fromSeconds(this.state.selectedLimits.restrictionPeriod)
        });
    }

    private _onContinue = () => {

        this.setState({
            isValidationEnabled: true
        }, () => this._onAfterValidationEnabled());

    }

    private _performValidation(): boolean {
        if(!this.state.isValidationEnabled) {
            return true;
        }

        const errorMessages: Pick<IGamblingSessionSetupState, 'lossLimitsErrorMessage' | 'timeLimitsErrorMessage'> = {
            lossLimitsErrorMessage: null,
            timeLimitsErrorMessage: null
        };

        if(Check.isNullOrUndefined(this.state.selectedLimits.lossLimitAmount)) {
            errorMessages.lossLimitsErrorMessage = this.services.translation.translate('GAMBLING_SESSION_LOSS_LIMIT_REQUIRED_ERROR');
        }

        if(Check.isNullOrUndefined(this.state.selectedLimits.sessionDurationLimit)) {
            errorMessages.timeLimitsErrorMessage = this.services.translation.translate('GAMBLING_SESSION_TIME_LIMIT_REQUIRED_ERROR');
        }

        this.setState({
            ...this.state,
            ...errorMessages
        });

        return !Boolean(errorMessages.lossLimitsErrorMessage || errorMessages.timeLimitsErrorMessage);
    }

    private _onTimeLimitSelected = (value: NullableNumber) => {
        this.setState({
            selectedLimits: {
                ...this.state.selectedLimits,
                sessionDurationLimit: value ? value : null
            }
        }, () => this._performValidation());


    }

    private _onGameLimitSelected = (value: NullableNumber) => {
        this.setState({
            selectedLimits: {
                ...this.state.selectedLimits,
                lossLimitAmount: value
            }
        }, () => this._performValidation());
    }

    private _onIntervalReminderSelected = (value: NullableNumber) => {
        this.setState({
            selectedLimits: {
                ...this.state.selectedLimits,
                reminderInterval: value ? value : null
            }
        });
    }

    private _onRestrictionPeriodSelected = (value: NullableNumber) => {
        this.setState({
            selectedLimits: {
                ...this.state.selectedLimits,
                restrictionPeriod: value ? value : null
            }
        });
    }

    private _getRegulatoryText() {
        const responsibleGamingLinkText = this.services.translation.translate('GAMBLING_SESSION_LIMIT_RESPONSIBLE_GAMING_LINK');
        return this.services.translation.translate('GAMBLING_SESSION_LIMIT_REGULATORY_TEXT', {
            responsibleGamblingLink: `<a class="${GAMBLING_SESSION_LIMITS_RESPONSIBLE_GAMING_LINK}">${responsibleGamingLinkText}</a>`
        });
    }

    private _captureRegulatoryTextElement = (element: HTMLElement) => {
        const linkElement = element?.querySelector(`.${GAMBLING_SESSION_LIMITS_RESPONSIBLE_GAMING_LINK}`);
        linkElement?.addEventListener('click', () => {
            this.services.linksNavigation.openResponsibleGaming();
        })
    }

    render() {
        return (
            <GamblingSessionLimitsBox>
                <GamblingSessionMessageBox>
                    {this.services.translation.translate('GAMBLING_SESSION_MESSAGE')}
                </GamblingSessionMessageBox>
                <GamblingSessionWarnMessageBox>
                    {this.services.translation.translate('GAMBLING_SESSION_WARN_MESSAGE')}
                </GamblingSessionWarnMessageBox>
                <DividerBox />
                <DropDownsGroupBox>
                    <TimeLimitDropDownComponent
                        onItemSelected={this._onTimeLimitSelected}
                        errorMessage={this.state.timeLimitsErrorMessage}
                    />
                    <DropDownsSeparatorBox/>
                    <LossLimitDropDownComponent
                        onItemSelected={this._onGameLimitSelected}
                        errorMessage={this.state.lossLimitsErrorMessage}
                    />
                </DropDownsGroupBox>

                <DropDownsGroupBox>
                    <IntervalReminderDropDownComponent onItemSelected={this._onIntervalReminderSelected}/>
                    <DropDownsSeparatorBox/>
                    <RestrictionPeriodsDropDownComponent onItemSelected={this._onRestrictionPeriodSelected}/>
                </DropDownsGroupBox>

                <FooterBox>
                    <Button preset="primary" onClick={this._onContinue} fullWidth={false}>
                        {this.services.translation.translate('GAMBLING_SESSION_START')}
                    </Button>

                    <RegulatoryTextBox dangerouslySetInnerHTML={{__html: this._getRegulatoryText()}} ref={this._captureRegulatoryTextElement}/>
                    { this.props.lastBetActivity &&
                        <RegulatoryTextBox className={CyClass('cy-last-bet-date')}>{this.services.translation.translate('GAMBLING_SESSION_LAST_BET', {lastBetDate: this.props.lastBetActivity})}</RegulatoryTextBox>
                    }
                </FooterBox>

            </GamblingSessionLimitsBox>
        );
    }
}
